import {Dialog, DialogActions, DialogTitle} from "@/components/tailwind/dialog.tsx";
import {useEffect, useState} from "react";
import {Button} from "@/components/tailwind/button.tsx";
import {Input} from "@/components/tailwind/input.tsx";
import {useAuth} from "@/context/use-auth.tsx";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Subheading} from "@/components/tailwind/heading.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import ValidationAlert from "@/components/validation-alert.tsx";
import {useCommonTranslation} from "@/i18n/hooks";
import {useToast} from "@/hooks/use-toast.ts";
import {getMyAccount, requestEmailChange} from "@/services/my-account-services.ts";
import {Badge} from "@/components/tailwind/badge.tsx";
import log from "@/lib/logger";

interface ChangeEmailDialogProps {
    onClose?: () => void;
    isInitiallyOpen?: boolean;
}

type ChangeEmailFormData = {
    newEmail: string;
}

const schema = z.object({
    newEmail: z.string()
        .min(1, "changeEmail.errors.required")
        .pipe(z.email("changeEmail.errors.invalidFormat")),
});

export default function ChangeEmailDialog({
                                              onClose,
                                              isInitiallyOpen = false,
                                          }: ChangeEmailDialogProps) {
    const [isOpen, setIsOpen] = useState(isInitiallyOpen)
    const {token} = useAuth()
    const {toast} = useToast()
    const {t} = useCommonTranslation()
    const [currentEmail, setCurrentEmail] = useState<string | null>(null)
    const [emailVerified, setEmailVerified] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    const {
        register,
        handleSubmit,
        reset,
        setError,
        formState: {errors, isSubmitting},
    } = useForm<ChangeEmailFormData>({resolver: zodResolver(schema)});

    useEffect(() => {
        if (isOpen && token) {
            setIsLoading(true)
            getMyAccount(token)
                .then(account => {
                    setCurrentEmail(account.email)
                    setEmailVerified(account.emailVerified)
                })
                .catch(err => log.error("Failed to load account info:", err))
                .finally(() => setIsLoading(false))
        }
    }, [isOpen, token])

    function handleClose() {
        setIsOpen(false)
        reset({newEmail: ''})
        onClose?.()
    }

    async function onSubmit(data: ChangeEmailFormData) {
        try {
            await requestEmailChange(data.newEmail, token!)
            handleClose()
            toast({
                title: t("changeEmail.success.title"),
                description: t("changeEmail.success.description"),
                variant: "default",
            })
        } catch (error) {
            log.error("Error requesting email change:", error)
            setError("root.serverError", {
                type: "serverError",
                message: t("serverError.unknown"),
            })
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <DialogTitle>{t("changeEmail.title")}</DialogTitle>

                {errors.root?.serverError && (
                    <ValidationAlert message={errors.root.serverError.message || t("serverError.unknown")}/>
                )}

                {isLoading ? (
                    <Text>{t("loading")}</Text>
                ) : (
                    <div className="space-y-6">
                        <div className="grid gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading>{t("changeEmail.currentEmail.heading")}</Subheading>
                                <Text>{t("changeEmail.currentEmail.description")}</Text>
                            </div>
                            <div className="flex items-center gap-2">
                                <Text className="font-medium">
                                    {currentEmail || t("changeEmail.currentEmail.notSet")}
                                </Text>
                                {currentEmail && (
                                    emailVerified ? (
                                        <Badge color="green" data-testid="email-verified-badge">
                                            {t("changeEmail.verified")}
                                        </Badge>
                                    ) : (
                                        <Badge color="amber" data-testid="email-unverified-badge">
                                            {t("changeEmail.unverified")}
                                        </Badge>
                                    )
                                )}
                            </div>
                        </div>

                        <div className="grid gap-y-6 sm:grid-cols-2">
                            <div className="space-y-1">
                                <Subheading>{t("changeEmail.newEmail.heading")}</Subheading>
                                <Text>{t("changeEmail.newEmail.description")}</Text>
                            </div>
                            <div>
                                <Input
                                    type="email"
                                    {...register("newEmail")}
                                    placeholder={t("changeEmail.newEmail.placeholder")}
                                    data-invalid={errors.newEmail || null}
                                    data-testid="change-email-input"
                                />
                                {errors.newEmail && (
                                    <ValidationAlert message={t(errors.newEmail.message || "serverError.unknown")}/>
                                )}
                            </div>
                        </div>

                        <div className="rounded-md bg-blue-50 dark:bg-blue-950 p-3">
                            <Text className="text-sm text-blue-700 dark:text-blue-300">
                                {t("changeEmail.hint")}
                            </Text>
                        </div>
                    </div>
                )}

                <DialogActions>
                    <Button
                        type="button"
                        plain
                        onClick={handleClose}
                        aria-label={t("buttons.cancel.ariaLabel")}
                    >
                        {t("buttons.cancel.text")}
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting || isLoading}
                        aria-label={t("changeEmail.submit.ariaLabel")}
                        data-testid="change-email-submit"
                    >
                        {isSubmitting ? t("buttons.processing") : t("changeEmail.submit.text")}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
